import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import { InputOTP } from '@shared/components/InputOTP';
import Layout from '@shared/components/Layout';
import {
  getAuthMfaVerificationFailureReasonCode,
  publishAuthMfaChallengeRequestCompletedEvent,
  publishAuthMfaChallengeRequestedEvent,
  publishAuthMfaVerificationFailedEvent,
  publishAuthMfaVerificationSubmittedEvent,
  type AuthFlow,
  type AuthMfaFlow,
} from '@shared/utils/analytics/events';
import { getMfaScreenLayoutProps, MfaScreenLayoutProps } from './layout';

interface Props extends MfaScreenLayoutProps {
  authFlow: AuthFlow;
  mfaFlow: AuthMfaFlow;
  onCodeSubmit: (code: string) => Promise<void>;
  onFormUpdate: (data: Record<string, unknown>) => void;
  errorText?: string;
  resetError: () => void;
}

const TotpVerificationScreen: React.FC<Props> = ({
  authFlow,
  mfaFlow,
  onCodeSubmit,
  onFormUpdate,
  errorText,
  resetError,
  variant,
  bannerImageDesktop,
  hideLogo,
  headerClasses,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const layoutProps = getMfaScreenLayoutProps({
    variant,
    bannerImageDesktop,
    hideLogo,
    headerClasses,
  });

  const publishVerificationFailed = useCallback(
    (error: unknown) => {
      publishAuthMfaVerificationFailedEvent(
        authFlow,
        getAuthMfaVerificationFailureReasonCode(error),
        'totp',
        mfaFlow
      );
    },
    [authFlow, mfaFlow]
  );

  useEffect(() => {
    publishAuthMfaChallengeRequestedEvent(
      authFlow,
      'initial_route',
      'totp',
      mfaFlow
    );
    publishAuthMfaChallengeRequestCompletedEvent(
      authFlow,
      'initial_route',
      'succeeded',
      'totp',
      mfaFlow
    );
  }, [authFlow, mfaFlow]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      otpInputRef.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (errorText && isSubmitting) {
      publishVerificationFailed(errorText);
      setIsSubmitting(false);
      setCode('');
    }
  }, [errorText, isSubmitting, publishVerificationFailed]);

  const submitCode = async (value: string) => {
    if (errorText) {
      resetError();
    }

    setIsSubmitting(true);
    onFormUpdate({ code: value });

    try {
      publishAuthMfaVerificationSubmittedEvent(authFlow, 'totp', mfaFlow);
      await onCodeSubmit(value);
    } catch (error) {
      publishVerificationFailed(error);
      setIsSubmitting(false);
      setCode('');
    }
  };

  const handleCodeChange = async (value: string) => {
    if (isSubmitting) {
      return;
    }

    setCode(value);

    if (errorText && value.length === 1) {
      resetError();
    }

    if (value.length === 6) {
      await submitCode(value);
    }
  };

  return (
    <Layout
      heading={t('Verify your identity')}
      subHeading={t(
        'Check your preferred one-time password application for a code.'
      )}
      {...layoutProps}
    >
      <div className="space-y-6">
        <div className="flex justify-center">
          <InputOTP
            ref={otpInputRef}
            maxLength={6}
            value={code}
            onChange={(value: string | number) =>
              handleCodeChange(String(value))
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            disabled={isSubmitting}
            autoFocus
          >
            <InputOTP.Group>
              <InputOTP.Slot index={0} />
              <InputOTP.Slot index={1} />
              <InputOTP.Slot index={2} />
              <InputOTP.Slot index={3} />
              <InputOTP.Slot index={4} />
              <InputOTP.Slot index={5} />
            </InputOTP.Group>
          </InputOTP>
        </div>

        {errorText && (
          <p className="text-red-600 text-center text-sm">{errorText}</p>
        )}

        <Button
          onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (code.length === 6 && !isSubmitting) {
              await submitCode(code);
            }
          }}
          disabled={!isSubmitting && code.length !== 6}
          state={isSubmitting ? 'loading' : 'default'}
          label={t('Submit')}
        />
      </div>
    </Layout>
  );
};

export default TotpVerificationScreen;
